import { useState, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { Combobox } from '@base-ui/react/combobox';
import { Search } from 'lucide-react';
import { PlatformGlyph } from '@/components/platform-glyph';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DisableForwardingDialog } from '@/components/disable-forwarding-dialog';
import { useWorkspace } from '@/contexts/workspace-context';
import { useSetChannelForwarding } from '@/components/channels/queries';
import { workspaceUrl } from '@/lib/workspace-url';
import { healthDotClass, healthTooltip } from '@/lib/health';
import { channelDisplayName } from '@/lib/channel-display';
import type { Channel } from '@/types/channel';
import { singularUnit, unitLabel } from '@/lib/usage-unit';

type ChannelFilter = 'connected' | 'disconnected' | 'all';

/** Sidebar channel-visibility filter options (base-ui Select needs the item list
 *  up front so the trigger resolves the selected label at mount). */
const CHANNEL_FILTER_ITEMS = [
  { label: 'Connected', value: 'connected' },
  { label: 'Disconnected', value: 'disconnected' },
  { label: 'All', value: 'all' },
];

/** Profile photo for the channel icon, or null to fall back to the platform glyph. */
function channelPhotoUrl(c: Channel): string | null {
  if (c.type === 'instagram') return c.instagramProfilePictureUrl;
  if (c.type === 'whatsapp') return c.whatsappProfilePictureUrl;
  return c.facebookPagePictureUrl;
}

/**
 * Channel icon: the IG/WA profile photo when we have one, falling back to the
 * platform glyph (also the safety net when a stored Meta CDN URL has expired and
 * the <img> 404s — base-ui Avatar swaps to the fallback). `after:hidden` drops
 * Avatar's default ring so the glyph fallback looks identical to a bare glyph.
 */
function ChannelIcon({ channel, className }: { channel: Channel; className?: string }) {
  const photo = channelPhotoUrl(channel);
  return (
    <Avatar className={cn('size-4 after:hidden', className)}>
      {photo ? <AvatarImage src={photo} alt="" /> : null}
      <AvatarFallback className="bg-transparent p-0">
        <PlatformGlyph type={channel.type} className="size-4" />
      </AvatarFallback>
    </Avatar>
  );
}

export function ChannelList() {
  const { activeWorkspace, channels, usage, refreshChannels } = useWorkspace();
  const { channelId } = useParams();
  const navigate = useNavigate();
  const { state: sidebarState } = useSidebar();
  const collapsed = sidebarState === 'collapsed';

  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>('connected');
  const [disableTarget, setDisableTarget] = useState<Channel | null>(null);

  // Separate instances so the per-row enable switch and the disable dialog
  // each track their own isPending.
  const enableForwarding = useSetChannelForwarding();
  const disableForwarding = useSetChannelForwarding();

  // Default view hides lifecycle-disconnected channels; the toggle (expanded
  // view only) reveals them. Shared by the expanded list AND the collapsed
  // icon rail so both honor the default-hide.
  const visibleChannels = useMemo(() => {
    if (channelFilter === 'all') return channels;
    if (channelFilter === 'disconnected')
      return channels.filter((c) => c.channelStatus === 'disconnected');
    return channels.filter((c) => c.channelStatus !== 'disconnected');
  }, [channels, channelFilter]);

  const filtered = useMemo(() => {
    if (!search.trim()) return visibleChannels;
    const q = search.toLowerCase();
    return visibleChannels.filter((a) =>
      channelDisplayName(a).toLowerCase().includes(q),
    );
  }, [visibleChannels, search]);

  // Collapsed icon rail. The full list/search/toggles can't fit a 64px rail, so
  // we show a search icon (opens the same search in a popup, mirroring the
  // workspace switcher) followed by one platform-glyph icon per channel, each a
  // link to its detail page with a health dot. Forwarding toggles live only in
  // the expanded view — there's no room for a switch in the rail.
  if (collapsed) {
    if (channels.length === 0) return null;
    return (
      <div className="flex flex-col items-center gap-1 px-1.5 py-2">
        <Combobox.Root
          items={filtered}
          filter={null}
          open={searchOpen}
          onOpenChange={(next) => {
            setSearchOpen(next);
            if (!next) setSearch('');
          }}
          onInputValueChange={(value) => setSearch(value)}
          onValueChange={(item) => {
            const ch = item as Channel | null;
            if (ch && activeWorkspace) {
              setSearchOpen(false);
              navigate(workspaceUrl(activeWorkspace.id, `channels/${ch.id}`));
            }
          }}
          itemToStringLabel={(item: Channel) =>
            item ? channelDisplayName(item) : ''
          }
          isItemEqualToValue={(a: Channel, b: Channel) => a?.id === b?.id}
        >
          <Combobox.Trigger
            render={
              <button
                type="button"
                aria-label="Search channels"
                title="Search channels"
                className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-foreground data-[popup-open]:bg-sidebar-accent data-[popup-open]:text-foreground"
              />
            }
          >
            <Search className="size-4" />
          </Combobox.Trigger>
          <Combobox.Portal>
            <Combobox.Positioner positionMethod="fixed" align="start" side="right" sideOffset={8}>
              <Combobox.Popup className="z-50 w-60 rounded-lg bg-popover p-1 text-popover-foreground shadow-md ring-1 ring-foreground/10 outline-none data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95">
                <div className="flex items-center gap-2 rounded-md px-2 py-1.5">
                  <Search className="size-4 shrink-0 text-muted-foreground" />
                  <Combobox.Input
                    placeholder="Search channels..."
                    className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                  />
                </div>
                <div className="-mx-1 my-1 h-px bg-foreground/10" />
                <Combobox.Empty className="px-2 py-6 text-center text-sm text-muted-foreground empty:hidden">
                  No channels match
                </Combobox.Empty>
                <Combobox.List className="max-h-64 overflow-y-auto no-visible-scrollbar">
                  {(item: Channel) => (
                    <Combobox.Item
                      key={item.id}
                      value={item}
                      className="flex cursor-pointer items-center gap-2 rounded-md p-2 text-sm outline-none select-none data-highlighted:bg-accent data-highlighted:text-accent-foreground"
                    >
                      <span className="relative flex size-2 shrink-0">
                        <span
                          className={`rounded-full ${healthDotClass(item)} size-2`}
                        />
                      </span>
                      <ChannelIcon channel={item} className="shrink-0" />
                      <span className="flex-1 truncate">
                        {channelDisplayName(item)}
                      </span>
                    </Combobox.Item>
                  )}
                </Combobox.List>
              </Combobox.Popup>
            </Combobox.Positioner>
          </Combobox.Portal>
        </Combobox.Root>

        {visibleChannels.map((channel) => {
          const name = channelDisplayName(channel);
          const isActive = channelId === channel.id && channels.length > 1;
          const isDisabled = !channel.forwardingEnabled;
          return (
            <Link
              key={channel.id}
              to={
                activeWorkspace
                  ? workspaceUrl(activeWorkspace.id, `channels/${channel.id}`)
                  : '#'
              }
              title={isDisabled ? `${name} (disabled)` : name}
              aria-label={name}
              className={`relative flex size-8 items-center justify-center rounded-md transition-colors ${isActive ? 'bg-sidebar-accent' : 'hover:bg-sidebar-accent/50'}`}
            >
              <ChannelIcon
                channel={channel}
                className={isDisabled ? 'opacity-50' : ''}
              />
              <span
                className={`absolute right-0.5 bottom-0.5 size-1.5 rounded-full ring-2 ring-sidebar ${healthDotClass(channel)}`}
              />
            </Link>
          );
        })}
      </div>
    );
  }

  // Expanded view always renders the group (the "Channels" header + filter +
  // search box stay even with zero channels, so the section reads as a stable
  // home for channels rather than vanishing — the filter especially must not
  // disappear, or there's no way to switch it back once it hides everything).
  // The collapsed icon rail still returns null above — a lone search icon over
  // nothing has no use in a 64px rail.

  return (
    <SidebarGroup className="flex flex-col min-h-0 flex-1 p-0">
      <SidebarGroupLabel className="h-auto shrink-0 px-4 pb-0">
        Channels
        {channels.length > 0 && (
          <span className="ml-1.5 text-xs text-muted-foreground tabular-nums">
            {channels.length}
          </span>
        )}
        <Select
          items={CHANNEL_FILTER_ITEMS}
          value={channelFilter}
          onValueChange={(v) => setChannelFilter(v as ChannelFilter)}
        >
          <SelectTrigger
            size="sm"
            aria-label="Filter channels"
            className="my-1 ml-auto h-6 gap-1 border-none bg-transparent pl-1.5 pr-0 text-xs font-normal text-muted-foreground shadow-none hover:text-foreground dark:bg-transparent dark:hover:bg-transparent"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="connected">Connected</SelectItem>
            <SelectItem value="disconnected">Disconnected</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </SidebarGroupLabel>
      <div className="relative px-4 pb-1">
        <Search className="absolute left-6.5 top-2 size-4 text-muted-foreground" />
        <Input
          placeholder="Search channels..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setSearch('');
          }}
          className="h-8 w-full bg-background pl-8 shadow-none"
        />
      </div>
      <SidebarMenu className="flex-1 min-h-0 overflow-y-auto no-visible-scrollbar">
        {filtered.length === 0 && search.trim() && (
          <p className="px-4 py-2 text-xs text-muted-foreground">
            No channels match
          </p>
        )}
        {filtered.map((channel) => {
          const dotClass = healthDotClass(channel);
          const tooltip = healthTooltip(channel);
          const name = channelDisplayName(channel);
          // With a single channel there's nothing to distinguish it from, so
          // the selected highlight is just noise — only highlight when 2+.
          const isActive = channelId === channel.id && channels.length > 1;
          const isDisabled = !channel.forwardingEnabled;
          const tooltipText = isDisabled ? `${name} (disabled)` : name;
          const chUsage = usage?.channels.find((u) => u.channelId === channel.id);

          return (
            <SidebarMenuItem key={channel.id}>
              <Link
                to={
                  activeWorkspace
                    ? workspaceUrl(activeWorkspace.id, `channels/${channel.id}`)
                    : '#'
                }
                title={tooltipText}
                className={`block px-4 py-1.5 ${isActive ? 'bg-sidebar-accent text-sidebar-accent-foreground' : 'hover:bg-sidebar-accent/50'}`}
              >
                <div className="flex items-center gap-1 w-full">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="relative flex size-2 shrink-0">
                      <span className={`rounded-full ${dotClass} size-2`} />
                      <span className="sr-only">{tooltip}</span>
                    </span>
                    <ChannelIcon
                      channel={channel}
                      className={`shrink-0 ${isDisabled ? 'opacity-60' : ''}`}
                    />
                    <span title={name} className={`truncate text-sm ${isDisabled ? 'opacity-60' : ''}`}>{name}</span>
                  </div>
                  {channel.channelStatus === 'disconnected' ? (
                    <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-950 dark:text-red-300">
                      Disconnected
                    </span>
                  ) : channel.metaConnected ? (
                    <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                      <Switch
                        size="sm"
                        checked={channel.forwardingEnabled}
                        aria-label={`Toggle messaging for ${name}`}
                        onCheckedChange={(checked) => {
                          if (!checked) {
                            setDisableTarget(channel);
                            return;
                          }
                          enableForwarding.mutate(
                            { channelId: channel.id, enabled: true },
                            {
                              onSuccess: () => {
                                toast.success('Messaging enabled');
                                refreshChannels();
                              },
                              onError: () => toast.error('Failed to enable'),
                            },
                          );
                        }}
                      />
                    </div>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-950 dark:text-red-300">
                      Disconnected
                    </span>
                  )}
                </div>
                <div className="pl-4 text-xs text-muted-foreground">
                  {chUsage && chUsage.total > 0
                    ? `${chUsage.total.toLocaleString()} ${singularUnit(usage?.usageUnit, chUsage.total)} this quota period`
                    : `No ${unitLabel(usage?.usageUnit)}`}
                </div>
              </Link>
            </SidebarMenuItem>
          );
        })}
      </SidebarMenu>

      <DisableForwardingDialog
        channelName={disableTarget ? channelDisplayName(disableTarget) : ''}
        confirmToken={
          disableTarget?.type === 'whatsapp'
            ? (disableTarget.whatsappDisplayPhoneNumber ?? channelDisplayName(disableTarget))
            : disableTarget
            ? channelDisplayName(disableTarget)
            : ''
        }
        open={!!disableTarget}
        onOpenChange={(open) => { if (!open) setDisableTarget(null); }}
        onConfirm={() => {
          if (!disableTarget) return;
          disableForwarding.mutate(
            { channelId: disableTarget.id, enabled: false },
            {
              onSuccess: () => {
                toast.success('Messaging disabled');
                setDisableTarget(null);
                refreshChannels();
              },
              onError: () => toast.error('Failed to disable messaging. Please try again.'),
            },
          );
        }}
        loading={disableForwarding.isPending}
      />
    </SidebarGroup>
  );
}
